import React, { useState } from 'react';
import fetch from 'isomorphic-unfetch';

import PropTypes from 'prop-types';
import classnames from 'classnames';
import css from 'styled-jsx/css';

import { mixed, strings, validate } from '../common/forms/Validations';
import Button from '../common/elements/Button';
import CountrySelector from '../common/elements/CountrySelector';
import Form from '../common/forms/Form';
import Overlay from '../common/elements/Overlay';
import subscriptions from '../../services/api/subscriptions';

const MOZILLA_LOCATION_SERVICE_ENABLED = false;
const FORM_MAX_WIDTH_MOBILE = '500px';

const autosuggestStyles = css.resolve`
  .autosuggest.autosuggest {
    position: relative;
    margin: 0;
    flex-grow: 1;
  }

  @media (min-width: ${FORM_MAX_WIDTH_MOBILE}) {
    .autosuggest.autosuggest {
      padding-right: 10px;
    }
  }
`;

const formInputStyles = css.resolve`
  .form__input.form__input {
    margin-bottom: 0;
  }
`;

const suggestionsStyles = css.resolve`
  .country-change-list.country-change-list {
    position absolute;
    margin-top: 0;
    top: 100%;
    width: 100%;
    max-height: 65vh;
    background-color: white;
    overflow: auto;
  }
`;

const suggestionsButtonStyles = css.resolve`
  .country-change-button-manual {
    margin: 30px auto 0;
    min-width: 100px;
  }
`;

const countryChangeStyles = css`
  .country-change-form {
    display: flex;
    width: 100%;
  }

  @media (max-width: ${FORM_MAX_WIDTH_MOBILE}) {
    .country-change-form {
      flex-direction: column;
    }
  }
  .country-change-question {
    margin-bottom: 20px;
    font-size: 2rem;
    line-height: 1.2;
  }
  .geolocation-button {
    padding-bottom: 3px;
    font-weight: 700;
    background: none;
    border: none;
    border-bottom: 5px solid white;
  }
`;

const countriesValidator = countries => {
  return {
    country: [
      strings.required.withError(''),
      mixed.oneOf(...countries.map(c => c.label))
    ]
  };
};

const countryFromLatLng = async (latitude, longitude) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&zoom=1&addressdetails=1`
    );
    if (response.ok) {
      const data = await response.json();
      return data.display_name;
    } else {
      return '';
    }
  } catch (err) {
    return '';
  }
};

const countryFromMozLocation = async () => {
  try {
    const response = await fetch(
      'https://location.services.mozilla.com/v1/geolocate?key=test'
    );
    const data = await response.json();
    const { lat, lng } = data.location;
    const country = await countryFromLatLng(lat, lng);
    return country;
  } catch (err) {
    return '';
  }
};
const EMPTY_DESTINATION = Object.freeze({
  country: '',
  latitude: '',
  longitude: ''
});
const CountryChange = ({
  countries,
  subscriptionId,
  onClose,
  onCountryChange
}) => {
  const [{ statusMessage, destination }, setState] = useState({
    statusMessage: 'notif-warning',
    destination: { ...EMPTY_DESTINATION }
  });

  const changeCountryFormSubmitted = async (formValues, action) => {
    action.setSubmitting(true);
    setState(state => {
      return { ...state, statusMessage: 'updating' };
    });
    const newDestination =
      formValues.country === destination.country
        ? destination
        : { country: formValues.country, latitude: '', longitude: '' };
    const res = await subscriptions.updateCountry(
      subscriptionId,
      newDestination
    );
    if (res.ok) {
      const subscription = await res.json();
      onCountryChange(subscription);
      setState(state => {
        return {
          ...state,
          destination: {
            ...EMPTY_DESTINATION,
            country: subscription.stay.destination
          },
          statusMessage: 'country-updated'
        };
      });
    } else {
      setState(state => {
        return { ...state, statusMessage: 'error' };
      });
    }
  };
  const updateCountry = (country, latitude, longitude) => {
    const newDestination =
      country.length > 0
        ? { country, latitude, longitude }
        : { ...destination };
    setState({
      statusMessage: 'country-form',
      destination: newDestination
    });
  };

  const launchGeolocation = () => {
    const onSuccess = async res => {
      setState(state => {
        return { ...state, statusMessage: 'waiting-for-country' };
      });

      const { latitude, longitude } = res.coords;
      const country = await countryFromLatLng(latitude, longitude);
      updateCountry(country, latitude, longitude);
    };
    const onError = async () => {
      const country = MOZILLA_LOCATION_SERVICE_ENABLED
        ? await countryFromMozLocation()
        : '';
      updateCountry(country);
    };
    navigator.geolocation.getCurrentPosition(onSuccess, onError);
  };

  return (
    <>
      <Overlay onClose={onClose}>
        {(overlayClassName, closeOverlay) => (
          <>
            {statusMessage === 'notif-warning' ? (
              <>
                <p className={classnames(overlayClassName, 'overlay-text')}>
                  Afin de transférer votre abonnement Yupwego vers un nouveau
                  pays, nous avons besoin de vous géolocaliser. Si vous refusez
                  la notification de géolocalisation automatique qui va suivre,
                  vous pourrez tout de même saisir manuellement le nom du pays
                  dans lequel vous vous trouvez.
                </p>
                <Button
                  key="country-change-manual-geolocation"
                  className={classnames(overlayClassName, 'overlay-button')}
                  onClick={launchGeolocation}
                  secondary
                >
                  Ok
                </Button>
              </>
            ) : null}
            {statusMessage === 'waiting-for-country' ? (
              <p className={classnames(overlayClassName, 'overlay-text')}>
                Recherche du pays en cours…
              </p>
            ) : null}
            {statusMessage === 'country-form' ? (
              <>
                {destination.country === '' ? (
                  <p className="country-change-question">
                    Dans quel pays vous trouvez-vous&nbsp;?
                  </p>
                ) : (
                  <p className="country-change-question">
                    Il semblerait que vous soyez du côté de ce pays. Est-ce
                    exact&nbsp;?
                  </p>
                )}
                <Form
                  initialValues={destination}
                  validate={value =>
                    validate(value, countriesValidator(countries))
                  }
                  onSubmit={changeCountryFormSubmitted}
                >
                  {({ values, metas, onChange, onBlur, doSubmit }) => (
                    <form className="country-change-form" onSubmit={doSubmit}>
                      {countries ? (
                        <CountrySelector
                          label="Pays"
                          className={classnames(
                            'autosuggest',
                            autosuggestStyles.className
                          )}
                          suggestionsClassName={classnames(
                            suggestionsStyles.className,
                            'country-change-list'
                          )}
                          inputClassName={formInputStyles.className}
                          value={values.country}
                          name="country"
                          valid={metas.country.valid}
                          error={metas.country.error}
                          required
                          onChange={onChange}
                          onBlur={onBlur}
                          countries={countries}
                          autofocus
                        />
                      ) : (
                        <p>Chargement des pays en cours.</p>
                      )}
                      <Button type="submit">Valider</Button>
                    </form>
                  )}
                </Form>
              </>
            ) : null}
            {statusMessage === 'updating' ? (
              <p className={classnames(overlayClassName, 'overlay-text')}>
                Votre souscription est en cours d’acheminement vers votre
                nouvelle destination&nbsp;!
              </p>
            ) : null}
            {statusMessage === 'country-updated' ? (
              <>
                <p className={classnames(overlayClassName, 'overlay-text')}>
                  Le changement de pays a bien été effectué.{' '}
                  {destination.country} ? Yupwego&nbsp;!
                </p>
                <Button
                  className={classnames(
                    suggestionsButtonStyles.className,
                    'country-change-button-manual'
                  )}
                  onClick={closeOverlay}
                  secondary
                >
                  Retourner sur mon profil
                </Button>
              </>
            ) : null}
            {statusMessage === 'error' ? (
              <p className={classnames(overlayClassName, 'overlay-text')}>
                Nous n’avons pas pû mettre à jour votre souscription. Merci
                d’essayer à nouveau ou contactez le support.
              </p>
            ) : null}
            {autosuggestStyles.styles}
            {suggestionsStyles.styles}
            {formInputStyles.styles}
            {suggestionsButtonStyles.styles}
          </>
        )}
      </Overlay>
      <style jsx>{countryChangeStyles}</style>
    </>
  );
};

CountryChange.propTypes = {
  countries: PropTypes.array,
  subscriptionId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onCountryChange: PropTypes.func.isRequired
};

export default CountryChange;
